import dayjs from 'dayjs';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { StepStatus } from '/@/modules/guidancestep/enums/StepStatus';
import { PredefinedEstateRole } from '/@/modules/role/enums/PredefinedEstateRole';
import { StepState } from '/@/modules/guidancestep/enums/StepState';
import { GuidanceStepMeta } from '/@/modules/guidancestep/models/GuidanceStepMeta';
import Lang from '/@/modules/core/app/services/Lang';
import { MessageTemplate } from '@/modules/messenger/_modules/message/models/MessageTemplate';

export class GuidanceStep extends BaseEntityModel {
  /**
   * @var {string}
   */
  key;

  /**
   * @var {number}
   */
  modelId;

  /**
   * @var {string}
   */
  modelType;

  /**
   * @var {number}
   */
  estateId;

  /**
   * @var {string}
   */
  name;

  /**
   * @var {boolean}
   */
  isHidden;

  /**
   * @var {string}
   */
  description;

  /**
   * @var {number}
   */
  weight;

  /**
   * @var {number}
   */
  order;

  /**
   * @var {string}
   */
  stage;

  /**
   * @var {string}
   */
  state;

  /**
   * @var {string}
   */
  action;

  /**
   * @var {string}
   */
  status;

  /**
   * @var {*<GuidanceStepMeta>}
   */
  meta;

  /**
   * @var {GuidanceStep[]}
   */
  dependencies;

  /**
   * @var {number[]}
   */
  roleIds;

  /**
   * @var {boolean}
   */
  isDependentOnOtherUser;

  /**
   * @var {boolean}
   */
  isDependentOnStepInDifferentStage;

  /**
   * @var {string}
   */
  updatedAt;

  /**
   * @var {string}
   */
  firstAccessedAt;

  /**
   * @var {MessageTemplate}
   */
  messageTemplate;

  constructor(model = {}) {
    super(model);

    this.key = model.key;
    this.modelType = model.modelType;
    this.modelId = model.modelId;
    this.estateId = model.estateId;
    this.name = model.name;
    this.isHidden = model.isHidden;
    this.description = model.description;
    this.weight = model.weight;
    this.order = model.order;
    this.stage = model.stage;
    this.state = model.state;
    this.action = model.action;
    this.status = model.status;
    this.meta = GuidanceStepMeta.factory(model.key, model.meta);
    this.dependencies = model.dependencies || [];
    this.roleIds = model.roleIds || [];
    this.isDependentOnOtherUser = model.isDependentOnOtherUser || false;
    this.isDependentOnStepInDifferentStage = model.isDependentOnStepInDifferentStage || false;
    this.updatedAt = model.updatedAt;
    this.firstAccessedAt = model.firstAccessedAt;
    this.messageTemplate = MessageTemplate.mapModelOrNull(model.messageTemplate);
  }

  /**
   * @returns {string}
   */
  get isoFormattedUpdatedAt() {
    return dayjs(this.updatedAt).format(DateFormat.ISO);
  }

  /**
   * @returns {string}
   */
  get statusValue() {
    return StepStatus.getByKey(this.status)?.value;
  }

  /**
   * @returns {string}
   */
  get stateValue() {
    return StepState.getByKey(this.state)?.value;
  }

  /**
   * @returns {boolean}
   */
  get isDone() {
    return StepStatus.getDoneStatuses().includes(this.status);
  }

  /**
   * @returns {boolean}
   */
  get isCompleted() {
    return this.status === StepStatus.COMPLETED.key;
  }

  /**
   * @returns {boolean}
   */
  get isOnHold() {
    return this.status === StepStatus.ON_HOLD.key;
  }

  /**
   * @returns {boolean}
   */
  get isInvalid() {
    return this.status === StepStatus.INVALID.key;
  }

  /**
   * @returns {boolean}
   */
  get isUnderMaintenance() {
    return this.status === StepStatus.UNDER_MAINTENANCE.key;
  }

  /**
   * @returns {boolean}
   */
  get isPending() {
    return this.status === StepStatus.PENDING.key;
  }

  /**
   * @returns {boolean}
   */
  get isInProgress() {
    return this.status === StepStatus.IN_PROGRESS.key;
  }

  /**
   * @returns {boolean}
   */
  get isInTodo() {
    return this.status === StepStatus.TODO.key;
  }

  /**
   * @returns {string}
   */
  get statusClass() {
    if (this.isCompleted) {
      return 'has-background-success-light has-text-success';
    }

    if (this.isInProgress || this.isPending || this.isOnHold) {
      return 'has-background-warning-light has-text-warning';
    }

    if (this.isInvalid) {
      return 'has-background-grey-lighter has-text-grey-light';
    }

    return 'has-text-grey-light';
  }

  /**
   * @returns {boolean}
   */
  get hasIncompleteDependencies() {
    return !!this.dependencies.find((item) =>
      StepStatus.getIncompleteStatuses().includes(item.status)
    );
  }

  /**
   * @returns {boolean}
   */
  get isLocked() {
    return this.hasIncompleteDependencies || this.isInvalid;
  }

  /**
   * @returns {boolean}
   */
  get isEstateProfessionalStep() {
    return (
      this.roleIds.length === 1 && this.roleIds[0] === PredefinedEstateRole.ESTATE_PROFESSIONAL.id
    );
  }

  get stepIncludesEstateProfessional() {
    return this.roleIds.includes(PredefinedEstateRole.ESTATE_PROFESSIONAL.id);
  }

  /**
   * @returns {string}
   */
  get dependenciesText() {
    if (this.isDependentOnOtherUser) {
      return Lang.t('guidancestep.dependent-on-users');
    }

    if (this.isDependentOnStepInDifferentStage) {
      return Lang.t('guidancestep.dependent-step-in');
    }

    const orders = this.dependencies
      .map((a) => a.order)
      .sort()
      .join(', ');
    const stepWord =
      this.dependencies.length > 1
        ? Lang.t('guidancestep.d40569d4-d1d1-4f5c-bb39-88751592e08e')
        : Lang.t('guidancestep.step');
    const text = this.dependencies.length > 3 ? `multiple ${stepWord}` : `${stepWord} ${orders}`;

    return `${Lang.t('guidancestep.dependent-text-on', { text })}`;
  }

  /**
   * @returns {Promise<*>}
   */
  async getIllustration() {
    let illustration;
    try {
      const module = await import(
        `./../../../assets/illustrations/steps/custom/step-${this.key}.svg`
      );
      illustration = module.default;
    } catch (err) {
      try {
        const module = await import(
          `./../../../assets/illustrations/steps/actions/${this.action}.svg`
        );
        illustration = module.default;
      } catch (err) {
        const module = await import(
          './../../../assets/illustrations/steps/actions/document_sign.svg'
        );
        illustration = module.default;
      }
    }

    return illustration;
  }
}
